import {
  ActionIcon,
  Badge,
  Box,
  Button,
  ColorInput,
  Divider,
  FileButton,
  Group,
  MultiSelect,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { SUPPORTED_LANGUAGES, findLanguage } from "@/lib/languages";
import { getSidebarIcon } from "@/features/space/lib/sidebar-icons.tsx";
import { openIconPicker } from "@/features/space/components/sidebar-links-editor.tsx";
import { useTranslationStatus } from "@/features/translation/queries/translation-query.ts";
import {
  IconCopy,
  IconDeviceDesktop,
  IconDeviceMobile,
  IconExternalLink,
  IconMoon,
  IconRefresh,
  IconSun,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import { queryClient } from "@/main.tsx";
import {
  uploadSpaceIcon,
  uploadSpaceSocialPreview,
} from "@/features/attachments/services/attachment-service.ts";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import { useSpaceQuery } from "@/features/space/queries/space-query.ts";
import { updateSpace } from "@/features/space/services/space-service.ts";
import type {
  IBranding,
  IBrandingColors,
  ISpace,
} from "@/features/space/types/space.types.ts";
import {
  SidebarLinksEditor,
  type SidebarLink,
} from "@/features/space/components/sidebar-links-editor.tsx";

const DEFAULT_LIGHT: IBrandingColors = {
  primary: "#2563eb",
  tint: null,
  semantic: {
    info: "#3b82f6",
    success: "#00C950",
    warning: "#FE9A00",
    danger: "#FB2C36",
  },
  codeTheme: "default-light",
};

const DEFAULT_DARK: IBrandingColors = {
  primary: "#60a5fa",
  tint: null,
  semantic: {
    info: "#60a5fa",
    success: "#4ADE80",
    warning: "#FBBF24",
    danger: "#F87171",
  },
  codeTheme: "default-dark",
};

type PresetSiteStyles = Partial<IBranding["siteStyles"]>;
type PresetSidebarStyles = Partial<IBranding["sidebarStyles"]>;

const PRESETS: Record<
  string,
  {
    name: string;
    light: Partial<IBrandingColors>;
    dark: Partial<IBrandingColors>;
    siteStyles?: PresetSiteStyles;
    sidebarStyles?: PresetSidebarStyles;
  }
> = {
  default: {
    name: "Default",
    // Fumadocs neutral.css native values — keeps the stock look
    light: { primary: "hsl(0, 0%, 9%)" },
    dark: { primary: "hsl(0, 0%, 98%)" },
    siteStyles: { font: "Inter", monoFont: "IBM Plex Mono", cornerStyle: "rounded" },
    sidebarStyles: { background: "default" },
  },
  clean: {
    name: "Clean",
    light: { primary: "#2563eb" },
    dark: { primary: "#60a5fa" },
    siteStyles: { font: "Inter", monoFont: "IBM Plex Mono", cornerStyle: "rounded" },
    sidebarStyles: { background: "subtle" },
  },
  forest: {
    name: "Forest",
    light: { primary: "#059669" },
    dark: { primary: "#34d399" },
    siteStyles: { font: "Inter", monoFont: "JetBrains Mono", cornerStyle: "rounded" },
    sidebarStyles: { background: "tinted" },
  },
  sunset: {
    name: "Sunset",
    light: { primary: "#dc2626" },
    dark: { primary: "#f87171" },
    siteStyles: { font: "Manrope", monoFont: "Fira Code", cornerStyle: "soft" },
    sidebarStyles: { background: "subtle" },
  },
  royal: {
    name: "Royal",
    light: { primary: "#7c3aed" },
    dark: { primary: "#a78bfa" },
    siteStyles: { font: "Inter", monoFont: "IBM Plex Mono", cornerStyle: "rounded" },
    sidebarStyles: { background: "tinted" },
  },
  minimal: {
    name: "Minimal",
    light: { primary: "#111827" },
    dark: { primary: "#f9fafb" },
    siteStyles: { font: "Inter", monoFont: "JetBrains Mono", cornerStyle: "sharp" },
    sidebarStyles: { background: "default" },
  },
  warm: {
    name: "Warm",
    light: { primary: "#d97706" },
    dark: { primary: "#fbbf24" },
    siteStyles: { font: "Manrope", monoFont: "Fira Code", cornerStyle: "soft" },
    sidebarStyles: { background: "tinted" },
  },
};

function mergeBranding(space?: ISpace): IBranding {
  const b = space?.settings?.branding ?? {};
  return {
    basic: {
      title: b.basic?.title ?? null,
      iconLight: b.basic?.iconLight ?? null,
      iconDark: b.basic?.iconDark ?? null,
      logoLight: b.basic?.logoLight ?? null,
      logoDark: b.basic?.logoDark ?? null,
      socialPreview: b.basic?.socialPreview ?? null,
    },
    themePreset: b.themePreset ?? "clean",
    colors: {
      light: { ...DEFAULT_LIGHT, ...(b.colors?.light ?? {}) },
      dark: { ...DEFAULT_DARK, ...(b.colors?.dark ?? {}) },
    },
    modes: {
      showToggle: b.modes?.showToggle ?? true,
      defaultMode: b.modes?.defaultMode ?? "light",
    },
    cta: {
      enabled: b.cta?.enabled ?? false,
      label: b.cta?.label ?? "",
      href: b.cta?.href ?? "",
      labels: { ...(b.cta?.labels ?? {}) },
    },
    promo: {
      enabled: b.promo?.enabled ?? false,
      title: b.promo?.title ?? "",
      description: b.promo?.description ?? "",
      // Fixed two slots so the form stays stable.
      buttons: [0, 1].map((i) => ({
        icon: b.promo?.buttons?.[i]?.icon ?? (i === 0 ? "telegram" : "youtube"),
        label: b.promo?.buttons?.[i]?.label ?? "",
        sublabel: b.promo?.buttons?.[i]?.sublabel ?? "",
        href: b.promo?.buttons?.[i]?.href ?? "",
      })),
      i18n: { ...(b.promo?.i18n ?? {}) },
    },
    hero: { enabled: b.hero?.enabled ?? false },
    siteStyles: {
      font: b.siteStyles?.font ?? "Inter",
      monoFont: b.siteStyles?.monoFont ?? "IBM Plex Mono",
      icons: b.siteStyles?.icons ?? "regular",
      cornerStyle: b.siteStyles?.cornerStyle ?? "rounded",
      depthStyle: b.siteStyles?.depthStyle ?? "subtle",
      linkStyle: b.siteStyles?.linkStyle ?? "default",
      showPageActions: b.siteStyles?.showPageActions ?? true,
    },
    sidebarStyles: {
      background: b.sidebarStyles?.background ?? "default",
      listStyle: b.sidebarStyles?.listStyle ?? "default",
    },
  };
}

export default function SpacePublicSettings({ spaceId }: { spaceId: string }) {
  const { t } = useTranslation();
  const { data: space } = useSpaceQuery(spaceId);

  const [branding, setBranding] = useState<IBranding>(() => mergeBranding(space));
  const [isPublic, setIsPublic] = useState(space?.settings?.public ?? false);
  const [customDomain, setCustomDomain] = useState(space?.customDomain ?? "");
  const initialTranslation = (space?.settings as any)?.translation ?? null;
  const [trEnabled, setTrEnabled] = useState<boolean>(
    initialTranslation?.enabled === true,
  );
  const [trSource, setTrSource] = useState<string>(
    initialTranslation?.source ?? "en-US",
  );
  const [trTargets, setTrTargets] = useState<string[]>(
    Array.isArray(initialTranslation?.targets)
      ? initialTranslation.targets
      : [],
  );
  const [trAuto, setTrAuto] = useState<boolean>(
    initialTranslation?.auto !== false,
  );
  const [trEngine, setTrEngine] = useState<string>(
    initialTranslation?.engine ?? "deepl",
  );
  const [sidebarLinks, setSidebarLinks] = useState<SidebarLink[]>(() => {
    const raw = (space?.settings as any)?.sidebarLinks;
    return Array.isArray(raw) ? raw : [];
  });
  const [saving, setSaving] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [previewThemeMode, setPreviewThemeMode] = useState<"light" | "dark">(
    "light",
  );
  const [previewDevice, setPreviewDevice] = useState<"desktop" | "mobile">(
    "desktop",
  );
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [previewReady, setPreviewReady] = useState(false);

  const previewUrl =
    customDomain
      ? `https://${customDomain}/`
      : space?.slug
        ? `https://${space.slug}.commitdoc.com/`
        : null;

  // Only re-seed local form state when the user navigates to a DIFFERENT
  // space (`space.id` changes), or when the data loads for the first
  // time (first non-undefined `space`). React Query refetches that
  // produce a new object reference but the same id (focus refetch,
  // mutation invalidations elsewhere) must NOT override in-progress
  // local edits — that was wiping the theme preset / "Show theme
  // toggle" / etc. back to whatever the server last persisted.
  const seededIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!space) return;
    if (seededIdRef.current === space.id) return;
    seededIdRef.current = space.id;
    setBranding(mergeBranding(space));
    setIsPublic(space.settings?.public ?? false);
    setCustomDomain(space.customDomain ?? "");
    const tr = (space.settings as any)?.translation ?? null;
    setTrEnabled(tr?.enabled === true);
    setTrSource(tr?.source ?? "en-US");
    setTrTargets(Array.isArray(tr?.targets) ? tr.targets : []);
    setTrAuto(tr?.auto !== false);
    setTrEngine(tr?.engine ?? "deepl");
    const links = (space.settings as any)?.sidebarLinks;
    setSidebarLinks(Array.isArray(links) ? links : []);
  }, [space]);

  useEffect(() => {
    function onMsg(e: MessageEvent) {
      if (e.data?.type === "commitdoc:preview-ready") setPreviewReady(true);
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  useEffect(() => {
    if (!previewReady || !iframeRef.current?.contentWindow) return;
    iframeRef.current.contentWindow.postMessage(
      {
        type: "commitdoc:branding",
        branding,
        themeMode: previewThemeMode,
      },
      "*",
    );
  }, [branding, previewReady, previewThemeMode]);

  useEffect(() => {
    setPreviewReady(false);
  }, [previewKey, previewUrl]);

  const updateColor = (
    mode: "light" | "dark",
    field: keyof IBrandingColors,
    value: string,
  ) => {
    setBranding((prev) => ({
      ...prev,
      colors: {
        ...prev.colors,
        [mode]: { ...prev.colors[mode], [field]: value },
      },
    }));
  };

  const updateSemantic = (
    mode: "light" | "dark",
    field: keyof IBrandingColors["semantic"],
    value: string,
  ) => {
    setBranding((prev) => ({
      ...prev,
      colors: {
        ...prev.colors,
        [mode]: {
          ...prev.colors[mode],
          semantic: { ...prev.colors[mode].semantic, [field]: value },
        },
      },
    }));
  };

  const applyPreset = (key: string) => {
    const preset = PRESETS[key];
    if (!preset) return;
    setBranding((prev) => ({
      ...prev,
      themePreset: key,
      colors: {
        light: { ...prev.colors.light, ...preset.light },
        dark: { ...prev.colors.dark, ...preset.dark },
      },
      siteStyles: preset.siteStyles
        ? { ...prev.siteStyles, ...preset.siteStyles }
        : prev.siteStyles,
      sidebarStyles: preset.sidebarStyles
        ? { ...prev.sidebarStyles, ...preset.sidebarStyles }
        : prev.sidebarStyles,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSpace({
        spaceId,
        branding,
        isPublic,
        customDomain: customDomain || null,
        // Translation settings live alongside branding in space.settings.
        // Server merges this jsonb and queues translation jobs for every
        // page × target on save when enabled+auto.
        // @ts-ignore — backend accepts `translation` on UpdateSpaceDto
        translation: {
          enabled: trEnabled,
          auto: trAuto,
          source: trSource,
          targets: trTargets,
          engine: trEngine,
        },
        // Sidebar pin-links — sent only when the user touched the section
        // so untouched legacy spaces keep their current list intact.
        // @ts-ignore — backend accepts `sidebarLinks` on UpdateSpaceDto
        sidebarLinks,
      } as Partial<ISpace>);
      notifications.show({
        message: t("Public settings saved"),
        color: "green",
      });
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.message ?? t("Failed to save"),
        color: "red",
      });
    } finally {
      setSaving(false);
    }
  };

  const previewToken = space?.settings?.previewToken ?? null;
  const previewLink =
    previewToken && previewUrl ? `${previewUrl}?preview=${previewToken}` : null;

  const mutatePreview = async (
    payload:
      | { regeneratePreviewToken: true }
      | { disablePreview: true },
    okMsg: string,
  ) => {
    setPreviewBusy(true);
    try {
      // @ts-ignore — backend accepts these on UpdateSpaceDto
      await updateSpace({ spaceId, ...payload } as Partial<ISpace>);
      await queryClient.invalidateQueries({ queryKey: ["space", spaceId] });
      notifications.show({ message: t(okMsg), color: "green" });
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.message ?? t("Failed to save"),
        color: "red",
      });
    } finally {
      setPreviewBusy(false);
    }
  };

  const copyPreviewLink = () => {
    if (!previewLink) return;
    navigator.clipboard?.writeText(previewLink);
    notifications.show({ message: t("Preview link copied"), color: "green" });
  };

  if (!space) return null;

  const iframeWidth = previewDevice === "mobile" ? 400 : "100%";

  return (
    <Box
      style={{
        display: "grid",
        gridTemplateColumns: "380px 1fr",
        gap: "1.5rem",
        alignItems: "flex-start",
      }}
    >
      {/* LEFT PANEL */}
      <Stack gap="md" style={{ minWidth: 0 }}>
        <Title order={3}>{t("Site customization")}</Title>

        <Stack gap="lg">
          <Divider label={t("General")} labelPosition="left" />
            <Stack gap="md">
              <Paper withBorder p="md">
                <Group justify="space-between">
                  <div>
                    <Text fw={500}>{t("Publish docs")}</Text>
                    <Text size="xs" c="dimmed">
                      {t("Make this space available on commitdoc.com")}
                    </Text>
                  </div>
                  <Switch
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.currentTarget.checked)}
                    size="lg"
                  />
                </Group>
              </Paper>

              <div>
                <Text size="sm" fw={500} mb={4}>
                  {t("Basic")}
                </Text>
                <Stack gap="sm">
                  <TextInput
                    label={t("Site title")}
                    placeholder={space.name}
                    value={branding.basic.title ?? ""}
                    onChange={(e) =>
                      setBranding((p) => ({
                        ...p,
                        basic: {
                          ...p.basic,
                          title: e.currentTarget.value || null,
                        },
                      }))
                    }
                  />

                  <BrandingAssetSlot
                    label={t("Icon (Light mode)")}
                    description={t(
                      "Shown as the browser tab favicon and next to the space name in the sidebar.",
                    )}
                    value={branding.basic.iconLight}
                    spaceId={spaceId}
                    onChange={(url) =>
                      setBranding((p) => ({
                        ...p,
                        basic: { ...p.basic, iconLight: url },
                      }))
                    }
                  />
                  <BrandingAssetSlot
                    label={t("Icon (Dark mode)")}
                    description={t(
                      "Used when the visitor has dark theme enabled.",
                    )}
                    value={branding.basic.iconDark}
                    spaceId={spaceId}
                    onChange={(url) =>
                      setBranding((p) => ({
                        ...p,
                        basic: { ...p.basic, iconDark: url },
                      }))
                    }
                  />
                  <BrandingAssetSlot
                    label={t("Logo (Light mode)")}
                    description={t("Wider brandmark shown in the nav.")}
                    value={branding.basic.logoLight}
                    spaceId={spaceId}
                    onChange={(url) =>
                      setBranding((p) => ({
                        ...p,
                        basic: { ...p.basic, logoLight: url },
                      }))
                    }
                    aspect="wide"
                  />
                  <BrandingAssetSlot
                    label={t("Logo (Dark mode)")}
                    description={t("Used when the visitor has dark theme enabled.")}
                    value={branding.basic.logoDark}
                    spaceId={spaceId}
                    onChange={(url) =>
                      setBranding((p) => ({
                        ...p,
                        basic: { ...p.basic, logoDark: url },
                      }))
                    }
                    aspect="wide"
                  />
                </Stack>
              </div>

              <Divider label={t("Themes")} />
              <Group gap="xs">
                {Object.entries(PRESETS).map(([key, preset]) => (
                  <Paper
                    key={key}
                    withBorder
                    p="xs"
                    style={{
                      cursor: "pointer",
                      borderColor:
                        branding.themePreset === key
                          ? preset.light.primary
                          : undefined,
                      borderWidth: branding.themePreset === key ? 2 : 1,
                      flex: "0 0 auto",
                    }}
                    onClick={() => applyPreset(key)}
                  >
                    <Group gap={6}>
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 4,
                          background: preset.light.primary,
                        }}
                      />
                      <Text size="xs">{preset.name}</Text>
                    </Group>
                  </Paper>
                ))}
              </Group>
            </Stack>
          <Divider label={t("Layout")} labelPosition="left" />
            <Stack gap="md">
              <Divider label={t("Light mode colors")} />
              <Group grow>
                <ColorInput
                  label={t("Primary")}
                  value={branding.colors.light.primary}
                  onChange={(v) => updateColor("light", "primary", v)}
                />
                <ColorInput
                  label={t("Info")}
                  value={branding.colors.light.semantic.info}
                  onChange={(v) => updateSemantic("light", "info", v)}
                />
              </Group>
              <Group grow>
                <ColorInput
                  label={t("Success")}
                  value={branding.colors.light.semantic.success}
                  onChange={(v) => updateSemantic("light", "success", v)}
                />
                <ColorInput
                  label={t("Warning")}
                  value={branding.colors.light.semantic.warning}
                  onChange={(v) => updateSemantic("light", "warning", v)}
                />
              </Group>
              <Group grow>
                <ColorInput
                  label={t("Danger")}
                  value={branding.colors.light.semantic.danger}
                  onChange={(v) => updateSemantic("light", "danger", v)}
                />
                <ColorInput
                  label={t("Tint")}
                  value={branding.colors.light.tint ?? ""}
                  onChange={(v) => updateColor("light", "tint", v)}
                />
              </Group>

              <Divider label={t("Dark mode")} />
              <ColorInput
                label={t("Primary (dark)")}
                value={branding.colors.dark.primary}
                onChange={(v) => updateColor("dark", "primary", v)}
              />

              <Divider label={t("Typography")} />
              <Group grow>
                <Select
                  label={t("Font")}
                  value={branding.siteStyles.font}
                  onChange={(v) =>
                    v &&
                    setBranding((p) => ({
                      ...p,
                      siteStyles: { ...p.siteStyles, font: v },
                    }))
                  }
                  data={["Inter", "Roboto", "System", "Geist", "Manrope"]}
                />
                <Select
                  label={t("Monospace")}
                  value={branding.siteStyles.monoFont}
                  onChange={(v) =>
                    v &&
                    setBranding((p) => ({
                      ...p,
                      siteStyles: { ...p.siteStyles, monoFont: v },
                    }))
                  }
                  data={["IBM Plex Mono", "JetBrains Mono", "Menlo", "Fira Code"]}
                />
              </Group>

              <Divider label={t("Shape")} />
              <Group grow>
                <Select
                  label={t("Corner style")}
                  value={branding.siteStyles.cornerStyle}
                  onChange={(v) =>
                    v &&
                    setBranding((p) => ({
                      ...p,
                      siteStyles: { ...p.siteStyles, cornerStyle: v },
                    }))
                  }
                  data={[
                    { value: "sharp", label: "Sharp" },
                    { value: "soft", label: "Soft" },
                    { value: "rounded", label: "Rounded" },
                    { value: "pill", label: "Pill" },
                  ]}
                />
                <Select
                  label={t("Icons")}
                  value={branding.siteStyles.icons}
                  onChange={(v) =>
                    v &&
                    setBranding((p) => ({
                      ...p,
                      siteStyles: { ...p.siteStyles, icons: v },
                    }))
                  }
                  data={[
                    { value: "regular", label: "Regular" },
                    { value: "solid", label: "Solid" },
                    { value: "outline", label: "Outline" },
                  ]}
                />
              </Group>

              <Divider label={t("Modes")} />
              <SegmentedControl
                fullWidth
                value={branding.modes.defaultMode}
                onChange={(v) =>
                  setBranding((p) => ({
                    ...p,
                    modes: {
                      ...p.modes,
                      defaultMode: v as "light" | "dark",
                    },
                  }))
                }
                data={[
                  { value: "light", label: t("Light") },
                  { value: "dark", label: t("Dark") },
                ]}
              />
              <Switch
                label={t("Show theme toggle")}
                checked={branding.modes.showToggle}
                onChange={(e) =>
                  setBranding((p) => ({
                    ...p,
                    modes: { ...p.modes, showToggle: e.currentTarget.checked },
                  }))
                }
              />

              <Divider label={t("Page header")} />
              <Switch
                label={t("Show page actions (Copy Markdown / Open)")}
                description={t(
                  "Action row under the page title on the public site",
                )}
                checked={branding.siteStyles.showPageActions}
                onChange={(e) =>
                  setBranding((p) => ({
                    ...p,
                    siteStyles: {
                      ...p.siteStyles,
                      showPageActions: e.currentTarget.checked,
                    },
                  }))
                }
              />

              <Divider label={t("Sidebar button")} />
              <Switch
                label={t("Show CTA button")}
                description={t(
                  "Accent button at the top of the public sidebar",
                )}
                checked={branding.cta.enabled}
                onChange={(e) =>
                  setBranding((p) => ({
                    ...p,
                    cta: { ...p.cta, enabled: e.currentTarget.checked },
                  }))
                }
              />
              <Group grow>
                <TextInput
                  label={t("Button label")}
                  placeholder={t("e.g. Personal account")}
                  value={branding.cta.label}
                  onChange={(e) =>
                    setBranding((p) => ({
                      ...p,
                      cta: { ...p.cta, label: e.currentTarget.value },
                    }))
                  }
                />
                <TextInput
                  label={t("Button URL")}
                  placeholder="https://…"
                  value={branding.cta.href}
                  onChange={(e) =>
                    setBranding((p) => ({
                      ...p,
                      cta: { ...p.cta, href: e.currentTarget.value },
                    }))
                  }
                />
              </Group>
              {branding.cta.enabled && trEnabled && trTargets.length > 0 ? (
                <Text size="xs" c="dimmed">
                  {t(
                    "The label is translated automatically into the configured languages on save",
                  )}
                </Text>
              ) : null}

              <Divider label={t("Home page")} />
              <Switch
                label={t("Custom home page (hero)")}
                description={t(
                  "Render a branded portal at the site root — banner, section cards, recently updated — instead of redirecting to the first article",
                )}
                checked={branding.hero.enabled}
                onChange={(e) =>
                  setBranding((p) => ({
                    ...p,
                    hero: { enabled: e.currentTarget.checked },
                  }))
                }
              />

              <Divider label={t("Promo block")} />
              <Switch
                label={t("Show promo block")}
                description={t(
                  "Card with title, description and tile buttons in the right column (under page contents)",
                )}
                checked={branding.promo.enabled}
                onChange={(e) =>
                  setBranding((p) => ({
                    ...p,
                    promo: { ...p.promo, enabled: e.currentTarget.checked },
                  }))
                }
              />
              {branding.promo.enabled ? (
                <Stack gap="sm">
                  <TextInput
                    label={t("Title")}
                    placeholder={t("e.g. Media hub")}
                    value={branding.promo.title}
                    onChange={(e) =>
                      setBranding((p) => ({
                        ...p,
                        promo: { ...p.promo, title: e.currentTarget.value },
                      }))
                    }
                  />
                  <TextInput
                    label={t("Description")}
                    placeholder={t("Short supporting line")}
                    value={branding.promo.description}
                    onChange={(e) =>
                      setBranding((p) => ({
                        ...p,
                        promo: {
                          ...p.promo,
                          description: e.currentTarget.value,
                        },
                      }))
                    }
                  />
                  {trEnabled && trTargets.length > 0 ? (
                    <Text size="xs" c="dimmed">
                      {t(
                        "Texts are translated automatically into the configured languages on save",
                      )}
                    </Text>
                  ) : null}
                  {branding.promo.buttons.map((btn, i) => {
                    const BtnIcon = getSidebarIcon(btn.icon);
                    const patchButton = (
                      patch: Partial<(typeof branding.promo.buttons)[number]>,
                    ) =>
                      setBranding((p) => ({
                        ...p,
                        promo: {
                          ...p.promo,
                          buttons: p.promo.buttons.map((b2, j) =>
                            j === i ? { ...b2, ...patch } : b2,
                          ),
                        },
                      }));
                    return (
                      <Paper key={i} withBorder p="sm">
                        <Group justify="space-between" mb="xs">
                          <Text size="sm" fw={500}>
                            {t("Button")} {i + 1}
                            {i === 0 ? ` — ${t("accented")}` : ""}
                          </Text>
                          <Tooltip label={t("Pick icon")} withArrow>
                            <ActionIcon
                              variant="default"
                              size="lg"
                              onClick={async () => {
                                const next = await openIconPicker(btn.icon);
                                if (next) patchButton({ icon: next });
                              }}
                            >
                              <BtnIcon size={18} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                        <Group grow>
                          <TextInput
                            label={t("Label")}
                            placeholder={i === 0 ? "Telegram" : "YouTube"}
                            value={btn.label}
                            onChange={(e) =>
                              patchButton({ label: e.currentTarget.value })
                            }
                          />
                          <TextInput
                            label={t("Sublabel")}
                            placeholder={t("e.g. Ops & updates")}
                            value={btn.sublabel}
                            onChange={(e) =>
                              patchButton({ sublabel: e.currentTarget.value })
                            }
                          />
                        </Group>
                        <TextInput
                          mt="xs"
                          label={t("URL")}
                          placeholder="https://…"
                          value={btn.href}
                          onChange={(e) =>
                            patchButton({ href: e.currentTarget.value })
                          }
                        />
                      </Paper>
                    );
                  })}
                </Stack>
              ) : null}

              <Divider label={t("Sidebar")} />
              <Group grow>
                <Select
                  label={t("Background")}
                  value={branding.sidebarStyles.background}
                  onChange={(v) =>
                    v &&
                    setBranding((p) => ({
                      ...p,
                      sidebarStyles: { ...p.sidebarStyles, background: v },
                    }))
                  }
                  data={[
                    { value: "default", label: "Default" },
                    { value: "subtle", label: "Subtle" },
                    { value: "tinted", label: "Tinted" },
                  ]}
                />
                <Select
                  label={t("List style")}
                  value={branding.sidebarStyles.listStyle}
                  onChange={(v) =>
                    v &&
                    setBranding((p) => ({
                      ...p,
                      sidebarStyles: { ...p.sidebarStyles, listStyle: v },
                    }))
                  }
                  data={[
                    { value: "default", label: "Default" },
                    { value: "compact", label: "Compact" },
                    { value: "spaced", label: "Spaced" },
                  ]}
                />
              </Group>
            </Stack>
          <Divider label={t("Sharing")} labelPosition="left" />
            <Stack gap="md">
              <Paper withBorder p="md">
                <Group justify="space-between">
                  <div>
                    <Text fw={500}>{t("Publish docs")}</Text>
                    <Text size="xs" c="dimmed">
                      {t("Anyone on the internet can view this space.")}
                    </Text>
                  </div>
                  <Switch
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.currentTarget.checked)}
                    size="lg"
                  />
                </Group>
              </Paper>
              <Text size="sm" c="dimmed">
                {t(
                  "When enabled, your docs are served at your subdomain or custom domain.",
                )}
              </Text>
              <BrandingAssetSlot
                label={t("Social preview")}
                description={t(
                  "Shown when pages are shared on social media (Telegram, Slack, Twitter, etc). 1200×630 (1.91:1), JPG/PNG under 1 MB; keep key content in the central 1080×566 safe zone.",
                )}
                value={branding.basic.socialPreview}
                spaceId={spaceId}
                onChange={(url) =>
                  setBranding((p) => ({
                    ...p,
                    basic: { ...p.basic, socialPreview: url },
                  }))
                }
                aspect="wide"
                fullSize
              />

              <Paper withBorder p="md">
                <Text fw={500}>{t("Preview link")}</Text>
                <Text size="xs" c="dimmed" mt={4}>
                  {t(
                    "A secret link to preview hidden pages and tabs in this published space before releasing them. Anyone with the link can view the hidden content.",
                  )}
                </Text>
                {previewToken ? (
                  <Stack gap="xs" mt="sm">
                    <TextInput
                      value={previewLink ?? ""}
                      readOnly
                      onFocusCapture={(e) => e.currentTarget.select()}
                    />
                    <Group gap="xs">
                      <Button
                        size="xs"
                        variant="default"
                        leftSection={<IconCopy size={14} />}
                        onClick={copyPreviewLink}
                        disabled={!previewLink}
                      >
                        {t("Copy")}
                      </Button>
                      <Button
                        size="xs"
                        variant="default"
                        leftSection={<IconRefresh size={14} />}
                        loading={previewBusy}
                        onClick={() =>
                          mutatePreview(
                            { regeneratePreviewToken: true },
                            "Preview link regenerated",
                          )
                        }
                      >
                        {t("Regenerate")}
                      </Button>
                      <Button
                        size="xs"
                        variant="subtle"
                        color="red"
                        leftSection={<IconTrash size={14} />}
                        loading={previewBusy}
                        onClick={() =>
                          mutatePreview(
                            { disablePreview: true },
                            "Preview link disabled",
                          )
                        }
                      >
                        {t("Disable")}
                      </Button>
                    </Group>
                    {!previewUrl ? (
                      <Text size="xs" c="dimmed">
                        {t(
                          "Save the space first so the preview link has a URL.",
                        )}
                      </Text>
                    ) : null}
                  </Stack>
                ) : (
                  <Button
                    size="xs"
                    variant="default"
                    mt="sm"
                    loading={previewBusy}
                    onClick={() =>
                      mutatePreview(
                        { regeneratePreviewToken: true },
                        "Preview link generated",
                      )
                    }
                  >
                    {t("Generate preview link")}
                  </Button>
                )}
              </Paper>
            </Stack>
          <Divider label={t("Configure")} labelPosition="left" />
            <Stack gap="md">
              <TextInput
                label={t("Subdomain")}
                description={t("Served at {{slug}}.commitdoc.com", {
                  slug: space.slug,
                })}
                value={`${space.slug}.commitdoc.com`}
                readOnly
              />
              <TextInput
                label={t("Custom domain")}
                description={t(
                  "Point a CNAME record to cname.commitdoc.com before saving",
                )}
                placeholder="docs.example.com"
                value={customDomain}
                onChange={(e) => setCustomDomain(e.currentTarget.value)}
              />
              {customDomain ? (
                <Paper withBorder p="sm">
                  <Text size="sm" fw={500}>
                    {t("DNS setup")}
                  </Text>
                  <Text size="xs" c="dimmed" mt={4}>
                    {t("Create a CNAME record:")}
                  </Text>
                  <Text size="xs" ff="monospace" mt={4}>
                    {customDomain} CNAME cname.commitdoc.com
                  </Text>
                </Paper>
              ) : null}
            </Stack>

          <Divider label={t("Translations")} labelPosition="left" />
            <Stack gap="md">
              <Paper withBorder p="md">
                <Group justify="space-between">
                  <div>
                    <Text fw={500}>{t("Auto-translate content")}</Text>
                    <Text size="xs" c="dimmed">
                      {t(
                        "Translate page titles and bodies into selected languages on every save (DeepL).",
                      )}
                    </Text>
                  </div>
                  <Switch
                    checked={trEnabled}
                    onChange={(e) => setTrEnabled(e.currentTarget.checked)}
                    size="lg"
                  />
                </Group>
              </Paper>

              {trEnabled ? (
                <Stack gap="sm">
                  <Select
                    label={t("Source language")}
                    description={t(
                      "Pages are written in this language; targets are produced from it.",
                    )}
                    value={trSource}
                    onChange={(v) => v && setTrSource(v)}
                    data={SUPPORTED_LANGUAGES.map((l) => ({
                      value: l.code,
                      label: `${l.flag}  ${l.name}`,
                    }))}
                    allowDeselect={false}
                    checkIconPosition="right"
                  />

                  <MultiSelect
                    label={t("Target languages")}
                    description={t(
                      "Switcher in the public sidebar shows source + targets.",
                    )}
                    value={trTargets}
                    onChange={setTrTargets}
                    data={SUPPORTED_LANGUAGES.filter(
                      (l) => l.code !== trSource,
                    ).map((l) => ({
                      value: l.code,
                      label: `${l.flag}  ${l.name}`,
                    }))}
                    searchable
                  />

                  <Select
                    label={t("Engine")}
                    value={trEngine}
                    onChange={(v) => v && setTrEngine(v)}
                    data={[{ value: "deepl", label: "DeepL" }]}
                    allowDeselect={false}
                  />

                  <Switch
                    label={t("Re-translate every time content changes")}
                    checked={trAuto}
                    onChange={(e) => setTrAuto(e.currentTarget.checked)}
                  />

                  <Text size="xs" c="dimmed">
                    {t(
                      "Saving here also queues a one-time backfill for all existing pages in this space.",
                    )}
                  </Text>

                  <TranslationProgress spaceId={spaceId} />
                </Stack>
              ) : null}
            </Stack>

          <Divider label={t("Sidebar links")} labelPosition="left" />
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                {t(
                  "Pinned shortcuts shown between the search field and page tree on the public sidebar. Use them for landing pages, social, GitHub, etc.",
                )}
              </Text>
              <SidebarLinksEditor
                value={sidebarLinks}
                onChange={setSidebarLinks}
              />
            </Stack>

        </Stack>

        <Group justify="flex-end" mt="md">
          <Button onClick={handleSave} loading={saving}>
            {t("Save")}
          </Button>
        </Group>
      </Stack>

      {/* RIGHT PANEL — PREVIEW */}
      <Box
        style={{
          position: "sticky",
          top: 16,
          background: "var(--mantine-color-gray-1)",
          borderRadius: 12,
          padding: "1rem",
        }}
      >
        <Group justify="space-between" mb="sm">
          <SegmentedControl
            size="xs"
            value={previewThemeMode}
            onChange={(v) => setPreviewThemeMode(v as "light" | "dark")}
            data={[
              {
                value: "light",
                label: (
                  <Group gap={4} wrap="nowrap">
                    <IconSun size={14} />
                  </Group>
                ),
              },
              {
                value: "dark",
                label: (
                  <Group gap={4} wrap="nowrap">
                    <IconMoon size={14} />
                  </Group>
                ),
              },
            ]}
          />
          <SegmentedControl
            size="xs"
            value={previewDevice}
            onChange={(v) => setPreviewDevice(v as "desktop" | "mobile")}
            data={[
              {
                value: "desktop",
                label: (
                  <Group gap={4} wrap="nowrap">
                    <IconDeviceDesktop size={14} />
                  </Group>
                ),
              },
              {
                value: "mobile",
                label: (
                  <Group gap={4} wrap="nowrap">
                    <IconDeviceMobile size={14} />
                  </Group>
                ),
              },
            ]}
          />
          <Group gap={4}>
            <Tooltip label={t("Refresh preview")}>
              <ActionIcon
                variant="subtle"
                onClick={() => setPreviewKey((k) => k + 1)}
                aria-label="refresh preview"
              >
                <IconRefresh size={16} />
              </ActionIcon>
            </Tooltip>
            {previewUrl ? (
              <Tooltip label={t("Open in new tab")}>
                <ActionIcon
                  variant="subtle"
                  component="a"
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="open preview"
                >
                  <IconExternalLink size={16} />
                </ActionIcon>
              </Tooltip>
            ) : null}
          </Group>
        </Group>

        {isPublic && previewUrl ? (
          <div
            style={{
              width: iframeWidth,
              maxWidth: "100%",
              margin: "0 auto",
              transition: "width 0.2s",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              borderRadius: 8,
              overflow: "hidden",
              background: "white",
            }}
          >
            {/* Browser chrome */}
            <div
              style={{
                background: "var(--mantine-color-dark-6)",
                padding: "8px 12px",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div style={{ display: "flex", gap: 6 }}>
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: "#ff5f56",
                  }}
                />
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: "#ffbd2e",
                  }}
                />
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: "#27c93f",
                  }}
                />
              </div>
              <div
                style={{
                  background: "rgba(255,255,255,0.1)",
                  color: "#aaa",
                  padding: "4px 10px",
                  borderRadius: 6,
                  flex: 1,
                  fontSize: 12,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {previewUrl}
              </div>
            </div>
            <iframe
              ref={iframeRef}
              key={previewKey}
              src={previewUrl}
              title="space preview"
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
              style={{
                width: "100%",
                height: "70vh",
                border: 0,
                display: "block",
                background: "white",
              }}
            />
          </div>
        ) : (
          <Text size="sm" c="dimmed" p="xl" ta="center">
            {isPublic
              ? t("Save the space first to enable the preview.")
              : t('Turn on "Publish docs" to enable the preview.')}
          </Text>
        )}
        <Text size="xs" c="dimmed" mt="xs" ta="center">
          {t(
            "Preview reloads on Save. Click refresh to re-render after publishing.",
          )}
        </Text>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// TranslationProgress — live status card for the Translations panel.
// Counts auto-refresh while jobs are pending; shows failed pages with
// language and error so editors can re-translate or fix the source.
// ---------------------------------------------------------------------------

function TranslationProgress({ spaceId }: { spaceId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useTranslationStatus(spaceId);
  if (isLoading || !data || !data.enabled) return null;

  const { summary, failed } = data;
  const stale = data.stale ?? [];
  const staleCount = summary.stale ?? 0;
  const ratio =
    summary.expected === 0
      ? 0
      : Math.round((summary.ready / summary.expected) * 100);

  const tone =
    summary.failed > 0
      ? "red"
      : summary.pending > 0
        ? "yellow"
        : staleCount > 0
          ? "orange"
          : "green";

  return (
    <Paper withBorder p="md">
      <Group justify="space-between" mb={6}>
        <Text fw={500} size="sm">
          {t("Translation progress")}
        </Text>
        <Badge color={tone} variant="light">
          {ratio}%
        </Badge>
      </Group>
      <Text size="xs" c="dimmed">
        {t(
          "{{ready}} of {{expected}} translations ready ({{pages}} pages × {{targets}} languages). {{pending}} pending, {{stale}} outdated, {{failed}} failed.",
          {
            ready: summary.ready,
            expected: summary.expected,
            pages: summary.totalPages,
            targets: data.expectedTargets,
            pending: summary.pending,
            stale: staleCount,
            failed: summary.failed,
          },
        )}
      </Text>

      {stale.length > 0 && (
        <Stack gap={4} mt="sm">
          <Text size="xs" fw={500} c="orange">
            {t("Outdated pages (edited after translation)")}
          </Text>
          {stale.slice(0, 8).map((f, i) => {
            const lang = findLanguage(f.language);
            return (
              <Group
                key={`${f.pageId}:${f.language}:${i}`}
                gap={6}
                wrap="nowrap"
                align="flex-start"
              >
                <Text size="xs" style={{ flex: "0 0 auto" }}>
                  {lang ? lang.flag : "🌐"}
                </Text>
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {f.title || f.publicSlug || f.slugId}
                </Text>
              </Group>
            );
          })}
          {stale.length > 8 && (
            <Text size="xs" c="dimmed">
              {t("+{{n}} more", { n: stale.length - 8 })}
            </Text>
          )}
        </Stack>
      )}

      {failed.length > 0 && (
        <Stack gap={4} mt="sm">
          <Text size="xs" fw={500} c="red">
            {t("Failed pages")}
          </Text>
          {failed.slice(0, 8).map((f, i) => {
            const lang = findLanguage(f.language);
            return (
              <Group
                key={`${f.pageId}:${f.language}:${i}`}
                gap={6}
                wrap="nowrap"
                align="flex-start"
              >
                <Text size="xs" style={{ flex: "0 0 auto" }}>
                  {lang ? lang.flag : "🌐"}
                </Text>
                <Stack gap={0} style={{ minWidth: 0 }}>
                  <Text size="xs" fw={500} truncate>
                    {f.title || t("Untitled")}
                  </Text>
                  <Text size="xs" c="dimmed" truncate>
                    {f.error ?? "—"}
                  </Text>
                </Stack>
              </Group>
            );
          })}
          {failed.length > 8 && (
            <Text size="xs" c="dimmed">
              + {failed.length - 8} {t("more")}
            </Text>
          )}
        </Stack>
      )}
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// BrandingAssetSlot — small upload widget for icon/logo slots in the
// customization panel. Stores the embedded (no-auth) URL so the public
// docs site can load it as favicon and sidebar icon.
// ---------------------------------------------------------------------------

function BrandingAssetSlot({
  label,
  description,
  value,
  spaceId,
  onChange,
  aspect,
  fullSize,
}: {
  label: string;
  description?: string;
  value: string | null;
  spaceId: string;
  onChange: (url: string | null) => void;
  aspect?: "square" | "wide";
  /** Skip the 300px icon downscale (social preview needs 1200×630). */
  fullSize?: boolean;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  const handleFile = async (file: File | null) => {
    if (!file) return;
    setBusy(true);
    try {
      const att = fullSize
        ? await uploadSpaceSocialPreview(file, spaceId)
        : await uploadSpaceIcon(file, spaceId);
      // Public-readable embedded URL — works for favicon + sidebar nav on
      // the public docs site without requiring auth.
      const url = `/api/files/embedded/${att.id}/${encodeURIComponent(att.fileName)}`;
      onChange(url);
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.message ?? t("Failed to upload image"),
        color: "red",
      });
    } finally {
      setBusy(false);
    }
  };

  const previewW = aspect === "wide" ? 96 : 48;
  const previewH = 48;

  return (
    <Group align="flex-start" gap="md" wrap="nowrap">
      <Box
        style={{
          width: previewW,
          height: previewH,
          borderRadius: 8,
          border: "1px solid var(--mantine-color-gray-3)",
          background: "var(--mantine-color-gray-0)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          flex: "0 0 auto",
        }}
      >
        {value ? (
          <img
            src={value}
            alt=""
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        ) : (
          <Text size="xs" c="dimmed">
            {t("None")}
          </Text>
        )}
      </Box>
      <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
        <Text size="sm" fw={500}>
          {label}
        </Text>
        {description ? (
          <Text size="xs" c="dimmed">
            {description}
          </Text>
        ) : null}
        <Group gap="xs" mt={4}>
          <FileButton
            onChange={handleFile}
            accept="image/png,image/jpeg,image/jpg,image/svg+xml"
            disabled={busy}
          >
            {(props) => (
              <Button
                {...props}
                size="xs"
                variant="default"
                leftSection={<IconUpload size={14} />}
                loading={busy}
              >
                {value ? t("Replace") : t("Upload")}
              </Button>
            )}
          </FileButton>
          {value ? (
            <Button
              size="xs"
              variant="subtle"
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={() => onChange(null)}
              disabled={busy}
            >
              {t("Remove")}
            </Button>
          ) : null}
        </Group>
      </Stack>
    </Group>
  );
}
